import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FileUpload } from '../components/Upload/FileUpload';
import { CheckCircle, ArrowRight } from 'lucide-react';

export const UploadPage: React.FC = () => {
  const [uploadResults, setUploadResults] = useState<any[]>([]);
  const navigate = useNavigate();

  const handleUploadComplete = (result: any) => {
    setUploadResults(prev => [...prev, result]);
  };

  const handleCreateStudyMaterials = () => {
    if (uploadResults.length > 0) {
      // Navigate to study tools with the processed content
      navigate('/study', { state: { studyContent: uploadResults } });
    }
  };

  return (
    <div className="h-full space-y-4">
      {/* Header - Compact */}
      <div className="text-center">
        <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-2">
          Upload Study Materials
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Upload PDFs, PowerPoint files, or other documents to generate AI-powered study materials
        </p>
      </div>

      {/* Upload Component */}
      <FileUpload onUploadComplete={handleUploadComplete} />

      {/* Processing Results - Compact */}
      {uploadResults.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center space-x-2 mb-3">
            <CheckCircle className="h-5 w-5 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Processing Complete!
            </h2>
          </div>
          
          <div className="space-y-3">
            {uploadResults.map((result, index) => (
              <div
                key={index}
                className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <h3 className="font-medium text-gray-900 dark:text-white mb-2 text-sm">
                  {result.fileName}
                </h3>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Flashcards:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">
                      {result.studyContent?.flashcards?.length || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Questions:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">
                      {result.studyContent?.questions?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 flex justify-center">
            <button
              onClick={handleCreateStudyMaterials}
              className="inline-flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <span>Start Studying</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Tips Section - Compact */}
      <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4 border border-primary-200 dark:border-primary-800">
        <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100 mb-2">
          Tips for Better Results
        </h3>
        <ul className="space-y-1 text-xs text-primary-700 dark:text-primary-300">
          <li>• Use clear, well-formatted documents for best AI processing</li>
          <li>• PDF files with selectable text work better than scanned images</li>
          <li>• Include lecture notes, textbook chapters, or study guides</li>
          <li>• Documents with headings and bullet points are processed more effectively</li>
          <li>• Keep files under 10MB for optimal processing speed</li>
        </ul>
      </div>
    </div>
  );
};